
import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"
import { imageUrlService } from '@/services/ImageUrlService'
import { debugLogger } from '@/utils/debugLogger'

import { cn } from "@/lib/utils"

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className
    )}
    {...props}
  />
))
Avatar.displayName = AvatarPrimitive.Root.displayName

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, src, onLoadingStatusChange, ...props }, ref) => {
  const [currentSrc, setCurrentSrc] = React.useState<string>("");
  const [fallbackUrls, setFallbackUrls] = React.useState<string[]>([]);
  const [fallbackIndex, setFallbackIndex] = React.useState<number>(0);

  React.useEffect(() => {
    if (src) {
      // ALWAYS use avatar variant
      const optimizedUrl = imageUrlService.getImageUrl(src, 'avatar');
      setCurrentSrc(optimizedUrl);
      
      // ALWAYS generate fallbacks with avatar context
      const fallbacks = imageUrlService.generateFallbackUrls(src, 'avatar');
      setFallbackUrls(fallbacks);
      setFallbackIndex(0);

      debugLogger.debug('AvatarImage', 'Initialized with avatar variant', {
        originalSrc: src,
        optimizedUrl,
        fallbackCount: fallbacks.length
      });
    }
  }, [src]);

  const handleError = React.useCallback(() => {
    if (fallbackIndex < fallbackUrls.length) {
      const nextUrl = fallbackUrls[fallbackIndex];
      debugLogger.warn('AvatarImage', `Avatar failed, trying fallback ${fallbackIndex + 1}/${fallbackUrls.length}`, {
        currentUrl: currentSrc,
        nextUrl
      });
      setCurrentSrc(nextUrl);
      setFallbackIndex(prev => prev + 1);
    } else {
      debugLogger.error('AvatarImage', 'All avatar fallbacks exhausted', {
        originalSrc: src,
        attemptedUrls: [currentSrc, ...fallbackUrls]
      });
    }
  }, [fallbackIndex, fallbackUrls, currentSrc, src]);

  return (
    <AvatarPrimitive.Image
      ref={ref}
      src={currentSrc}
      className={cn("aspect-square h-full w-full object-cover", className)}
      onLoadingStatusChange={onLoadingStatusChange}
      onError={handleError}
      {...props}
    />
  );
})
AvatarImage.displayName = AvatarPrimitive.Image.displayName

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className
    )}
    {...props}
  />
))
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

export { Avatar, AvatarImage, AvatarFallback }
